import { useState, useEffect, useCallback, useRef } from 'react';
import { SignedIn, useClerk } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '@clerk/clerk-react';
import { formatDateInUserTime } from '../utils/date';
import RewardsBar from '../components/RewardsBar';
import QRCode from 'qrcode';

const POLL_INTERVAL_MS = 5000;
const PERCENT_OFF_TYPES = ['10_percent_off', 'custom_percent'];

/** Format digits as (XXX) XXX-XXXX. Max 10 digits. */
function formatPhoneDisplay(value) {
  const digits = (value || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const STATUS_LABELS = {
  awaiting_payment: 'Confirming payment…',
  pending: 'Received',
  paid: 'Paid',
  in_progress: 'Preparing',
  done: 'Ready to pick up',
  picked_up: 'Picked up',
  refunded: 'Refunded',
};

/**
 * Dashboard / Customer hub. Tile-based layout for order status, account, past orders, menus.
 */
export default function Profile() {
  const { signOut } = useClerk();
  const { getToken } = useAuth();
  const [userType, setUserType] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);
  const [phoneSaved, setPhoneSaved] = useState(false);
  const [activeView, setActiveView] = useState(null); // null | 'account' | 'past-orders' | 'menus' | 'my-rewards' | 'my-rewards-detail'
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);
  const [rewardsSummary, setRewardsSummary] = useState(null);

  const fetchData = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    try {
      const [meRes, custRes, rewardsRes] = await Promise.all([
        api('/api/users/me', { getToken }),
        api('/api/customers/me', { getToken }),
        api('/api/customers/me/rewards-summary', { getToken }),
      ]);
      if (meRes.ok) {
        const me = await meRes.json();
        setUserType(me.userType);
      }
      if (custRes.ok) {
        const cust = await custRes.json();
        setProfile(cust);
        setPhone(cust.phone ? formatPhoneDisplay(cust.phone) : '');
      }
      if (rewardsRes.ok) {
        const rew = await rewardsRes.json();
        setRewardsSummary(rew?.businesses ?? []);
      }
    } catch (err) {
      console.warn('Profile fetch error', err);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  const fetchDataRef = useRef(fetchData);

  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await fetchData();
      if (!cancelled) setLoading(false);
    })();
    const interval = setInterval(() => {
      if (fetchDataRef.current) fetchDataRef.current();
    }, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && fetchDataRef.current) fetchDataRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchData]);

  const handleSavePhone = async (e) => {
    e.preventDefault();
    const digits = (phone || '').replace(/\D/g, '');
    if (digits.length < 10) return;
    setSavingPhone(true);
    try {
      const res = await api('/api/customers/me', {
        getToken,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: digits }),
      });
      if (res.ok) {
        setPhoneSaved(true);
        setTimeout(() => setPhoneSaved(false), 2000);
        await fetchData();
      } else {
        const d = await res.json();
        alert(d.error || 'Failed to save');
      }
    } catch (err) {
      alert(err.message || 'Failed to save');
    } finally {
      setSavingPhone(false);
    }
  };

  const visitedMenus = (() => {
    try {
      const s = localStorage.getItem('gng_visitedMenus');
      return s ? JSON.parse(s) : [];
    } catch {
      return [];
    }
  })();

  if (loading && !profile) {
    return (
      <SignedIn>
        <div className="flex items-center justify-center min-h-[40vh]">
          <p className="text-slate-600 dark:text-slate-400">Loading…</p>
        </div>
      </SignedIn>
    );
  }

  const orders = profile?.orders || [];
  const currentOrder = orders.find((o) =>
    ['pending', 'paid', 'in_progress', 'done'].includes((o.status || '').toLowerCase())
  );
  const pastOrders = orders.filter((o) =>
    ['picked_up', 'refunded'].includes(o.status)
  );

  // Show detail view when a tile is clicked
  if (activeView === 'account') {
    return (
      <SignedIn>
        <div className="space-y-6">
          <button
            type="button"
            onClick={() => setActiveView(null)}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            ← Back to Dashboard
          </button>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">
              Phone number
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
              We&apos;ll text you when your order is ready. Saved for future orders.
            </p>
            <form onSubmit={handleSavePhone} className="flex gap-3">
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(formatPhoneDisplay(e.target.value))}
                placeholder="(555) 123-4567"
                className="flex-1 px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
              />
              <button
                type="submit"
                disabled={savingPhone || (phone || '').replace(/\D/g, '').length < 10}
                className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium"
              >
                {savingPhone ? 'Saving…' : phoneSaved ? 'Saved!' : profile?.phone ? 'Edit' : 'Save'}
              </button>
            </form>
          </div>
        </div>
      </SignedIn>
    );
  }

  if (activeView === 'past-orders') {
    return (
      <SignedIn>
        <div className="space-y-6">
          <button
            type="button"
            onClick={() => setActiveView(null)}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            ← Back to Dashboard
          </button>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">
              Past orders
            </h2>
            {pastOrders.length === 0 ? (
              <p className="text-slate-500 dark:text-slate-400">No past orders yet. Orders appear here once they&apos;re picked up.</p>
            ) : (
              <div className="space-y-2">
                {pastOrders.slice(0, 10).map((o) => (
                  <Link
                    key={o.id}
                    to={`/order/${o.id}/success`}
                    className="block rounded-lg border border-slate-200 dark:border-slate-700 p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">{o.businessName}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {formatDateInUserTime(o.createdAt)} · {STATUS_LABELS[o.status] ?? o.status}
                        </p>
                      </div>
                      <p className="font-medium">${(o.totalCents / 100).toFixed(2)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </SignedIn>
    );
  }

  if (activeView === 'menus') {
    return (
      <SignedIn>
        <div className="space-y-6">
          <button
            type="button"
            onClick={() => setActiveView(null)}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            ← Back to Dashboard
          </button>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">
              Menus you&apos;ve visited
            </h2>
            {visitedMenus.length === 0 ? (
              <p className="text-slate-500 dark:text-slate-400">Browse menus to see them here.</p>
            ) : (
              <div className="flex flex-col gap-3">
                {visitedMenus.map((m) => (
                  <Link
                    key={m.slug}
                    to={`/m/${m.slug}`}
                    className="block px-4 py-3 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-900 dark:text-slate-100 text-sm font-medium transition-colors"
                  >
                    <span className="block">{m.name}</span>
                    {m.businessId && <RewardsBar businessId={m.businessId} getToken={getToken} />}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </SignedIn>
    );
  }

  // My Rewards — business tiles view
  if (activeView === 'my-rewards') {
    const businesses = rewardsSummary ?? [];
    return (
      <SignedIn>
        <div className="space-y-6">
          <button
            type="button"
            onClick={() => setActiveView(null)}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            ← Back to Dashboard
          </button>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
              My Rewards
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              Tap a spot to view or use your rewards
            </p>
            {businesses.length === 0 ? (
              <p className="text-slate-500 dark:text-slate-400 py-4">
                Order from menus to earn rewards. Your points will appear here.
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                {businesses.map((b) => (
                  <button
                    key={b.businessId}
                    type="button"
                    onClick={() => { setSelectedBusinessId(b.businessId); setActiveView('my-rewards-detail'); }}
                    className="block w-full text-left px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                  >
                    <span className="font-medium text-slate-900 dark:text-slate-100 block">{b.businessName}</span>
                    <div className="flex items-center gap-2 mt-1">
                      <RewardsBar businessId={b.businessId} getToken={getToken} />
                      {b.unredeemedClaim && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200">
                          1 reward to use
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </SignedIn>
    );
  }

  // My Rewards — per-business detail (QR / claim flow)
  if (activeView === 'my-rewards-detail' && selectedBusinessId) {
    return (
      <SignedIn>
        <MyRewardsDetailView
          businessId={selectedBusinessId}
          businesses={rewardsSummary ?? []}
          getToken={getToken}
          onBack={() => { setSelectedBusinessId(null); setActiveView('my-rewards'); }}
          onRefresh={fetchData}
        />
      </SignedIn>
    );
  }

  return (
    <SignedIn>
      <div className="space-y-8">
        <header className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            Dashboard
          </h1>
          <div className="flex items-center gap-3">
            {userType === 'owner' && (
              <Link
                to="/owner/dashboard"
                className="text-sm px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium"
              >
                Owner Dashboard
              </Link>
            )}
            <button
              type="button"
              onClick={() => signOut({ redirectUrl: userType === 'owner' ? '/' : '/customer' })}
              className="text-sm font-medium text-slate-600 dark:text-slate-400 hover:underline"
            >
              Sign out
            </button>
          </div>
        </header>

        {/* Tiles */}
        <div className="grid grid-cols-2 gap-4">
          {/* My Rewards tile - full width */}
          <button
            type="button"
            onClick={() => setActiveView('my-rewards')}
            className="col-span-2 rounded-2xl border-2 border-amber-300 dark:border-amber-600 bg-gradient-to-r from-amber-50 to-amber-100/80 dark:from-amber-900/30 dark:to-amber-800/20 p-6 min-h-[120px] flex flex-col items-center justify-center text-center hover:border-amber-400 dark:hover:border-amber-500 transition-colors w-full"
          >
            <span className="text-3xl">🎰</span>
            <div className="mt-2">
              <span className="font-semibold text-slate-900 dark:text-slate-100 block">
                My Rewards
              </span>
              <span className="text-sm text-amber-700 dark:text-amber-300">
                {(rewardsSummary ?? []).filter((b) => b.unredeemedClaim).length > 0
                  ? `${(rewardsSummary ?? []).filter((b) => b.unredeemedClaim).length} reward(s) to use`
                  : (rewardsSummary ?? []).length > 0
                    ? `${(rewardsSummary ?? [])[0].points ?? 0} pts at ${(rewardsSummary ?? [])[0].businessName}`
                    : 'Earn rewards at your favorite spots'}
              </span>
            </div>
          </button>

          {/* Order Status tile */}
          {currentOrder ? (
            <Link
              to={((currentOrder.status || '').toLowerCase() === 'awaiting_payment')
                ? `/order/${currentOrder.id}/success`
                : `/order/${currentOrder.id}/status`}
              className="relative rounded-2xl border-2 border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/20 p-6 min-h-[120px] flex flex-col items-center justify-center text-center hover:border-emerald-400 dark:hover:border-emerald-600 transition-colors"
            >
              <span className="text-3xl">📦</span>
              <div className="mt-2">
                <span className="font-semibold text-slate-900 dark:text-slate-100 block">
                  Order Status
                </span>
                <span className="text-sm text-emerald-700 dark:text-emerald-300">
                  {STATUS_LABELS[currentOrder.status] ?? currentOrder.status}
                </span>
              </div>
              <span className="absolute top-3 right-3 w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
            </Link>
          ) : (
            <div className="rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 min-h-[120px] flex flex-col items-center justify-center text-center">
              <span className="text-3xl">📦</span>
              <div className="mt-2">
                <span className="font-semibold text-slate-900 dark:text-slate-100 block">
                  Order Status
                </span>
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  No active order
                </span>
              </div>
            </div>
          )}

          {/* Account info tile */}
          <button
            type="button"
            onClick={() => setActiveView('account')}
            className="rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 min-h-[120px] flex flex-col items-center justify-center text-center hover:border-slate-300 dark:hover:border-slate-600 transition-colors w-full"
          >
            <span className="text-3xl">👤</span>
            <div className="mt-2">
              <span className="font-semibold text-slate-900 dark:text-slate-100 block">
                Account
              </span>
              <span className="text-sm text-slate-600 dark:text-slate-400 truncate block">
                {profile?.phone ? formatPhoneDisplay(profile.phone) : 'Add phone'}
              </span>
            </div>
          </button>

          {/* Past orders tile */}
          <button
            type="button"
            onClick={() => setActiveView('past-orders')}
            className="rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 min-h-[120px] flex flex-col items-center justify-center text-center hover:border-slate-300 dark:hover:border-slate-600 transition-colors w-full"
          >
            <span className="text-3xl">📋</span>
            <div className="mt-2">
              <span className="font-semibold text-slate-900 dark:text-slate-100 block">
                Past Orders
              </span>
              <span className="text-sm text-slate-600 dark:text-slate-400">
                {pastOrders.length} order{pastOrders.length !== 1 ? 's' : ''}
              </span>
            </div>
          </button>

          {/* Menus tile */}
          <button
            type="button"
            onClick={() => setActiveView('menus')}
            className="rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 min-h-[120px] flex flex-col items-center justify-center text-center hover:border-slate-300 dark:hover:border-slate-600 transition-colors w-full"
          >
            <span className="text-3xl">🍽️</span>
            <div className="mt-2">
              <span className="font-semibold text-slate-900 dark:text-slate-100 block">
                Menus
              </span>
              <span className="text-sm text-slate-600 dark:text-slate-400">
                {visitedMenus.length} saved
              </span>
            </div>
          </button>
        </div>

        {userType !== 'customer' && (
          <Link
            to="/"
            className="text-sm text-slate-500 dark:text-slate-400 hover:underline block"
          >
            ← Back to home
          </Link>
        )}
      </div>
    </SignedIn>
  );
}

/** Per-business My Rewards detail: claim flow, QR display, use in cart. */
function MyRewardsDetailView({ businessId, businesses, getToken, onBack, onRefresh }) {
  const biz = businesses.find((b) => b.businessId === businessId);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimView, setClaimView] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);
  const [redeemed, setRedeemed] = useState(false);
  const pollRef = useRef(null);

  const hasUnredeemed = !!biz?.unredeemedClaim;
  const canClaim = biz?.canClaim && !hasUnredeemed;
  const isPercentOff = biz?.unredeemedClaim && PERCENT_OFF_TYPES.includes(biz.unredeemedClaim.rewardType);
  const hasQr = hasUnredeemed && !isPercentOff;

  // Poll for redemption when QR is shown
  useEffect(() => {
    if (!hasQr || !biz?.unredeemedClaim?.id || redeemed) return;
    const poll = async () => {
      const res = await api('/api/customers/me/rewards-summary', { getToken });
      if (!res.ok) return;
      const d = await res.json();
      const updated = d.businesses?.find((b) => b.businessId === businessId);
      if (updated && !updated.unredeemedClaim) {
        setRedeemed(true);
        onRefresh?.();
      }
    };
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [hasQr, biz?.unredeemedClaim?.id, businessId, getToken, onRefresh, redeemed]);

  const fetchQr = useCallback(async () => {
    if (!biz?.unredeemedClaim?.id) return;
    setQrLoading(true);
    try {
      const res = await api(`/api/customers/me/rewards/${biz.unredeemedClaim.id}/qr-payload`, { getToken });
      if (!res.ok) return;
      const d = await res.json();
      if (d?.payload) {
        const url = await QRCode.toDataURL(d.payload, { width: 220, margin: 2 });
        setQrDataUrl(url);
      }
    } catch (_) {}
    finally {
      setQrLoading(false);
    }
  }, [biz?.unredeemedClaim?.id, getToken]);

  const handleShowQr = () => {
    if (!qrDataUrl) fetchQr();
    else setQrDataUrl(null);
  };

  const handleClaim = async () => {
    if (!selectedReward || !businessId) return;
    setClaiming(true);
    try {
      const body = {
        businessId,
        rewardType: selectedReward.type,
        menuItemId: selectedReward.menuItemId ?? undefined,
        customPercent: selectedReward.customPercent ?? undefined,
      };
      const res = await api('/api/customers/me/rewards/claim', {
        getToken,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        await onRefresh?.();
        setClaimView(false);
        setSelectedReward(null);
      } else {
        const d = await res.json();
        alert(d.error || 'Claim failed');
      }
    } catch (err) {
      alert(err.message || 'Claim failed');
    } finally {
      setClaiming(false);
    }
  };

  if (!biz) {
    return (
      <div className="space-y-6">
        <button type="button" onClick={onBack} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
          ← Back to Dashboard
        </button>
        <p className="text-slate-500 dark:text-slate-400">Business not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button type="button" onClick={onBack} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
        ← Back to My Rewards
      </button>
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3">{biz.businessName}</h2>

        {redeemed && (
          <div className="p-4 rounded-xl bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-center">
            Redeemed! Thanks for visiting.
          </div>
        )}

        {!redeemed && hasUnredeemed && (
          <>
            <p className="text-slate-700 dark:text-slate-300 font-medium mb-4">{biz.unredeemedClaim.label}</p>
            <div className="flex flex-col gap-3">
              <Link
                to={`/m/${biz.slug}`}
                className="block w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium text-center"
              >
                Use in cart
              </Link>
              {hasQr && (
                <>
                  {qrDataUrl ? (
                    <div className="space-y-3">
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        Show this to the owner at pickup.
                      </p>
                      <div className="flex justify-center p-4 bg-white dark:bg-slate-900 rounded-xl">
                        <img src={qrDataUrl} alt="Reward QR" className="w-[220px] h-[220px]" />
                      </div>
                      <button
                        type="button"
                        onClick={() => setQrDataUrl(null)}
                        className="text-sm text-slate-500 dark:text-slate-400 hover:underline"
                      >
                        Hide QR
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleShowQr}
                      disabled={qrLoading}
                      className="w-full py-3 rounded-xl border-2 border-amber-500 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 font-medium disabled:opacity-50"
                    >
                      {qrLoading ? 'Loading…' : 'Show QR at counter'}
                    </button>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {!redeemed && canClaim && (
          <>
            <p className="text-slate-700 dark:text-slate-300 mb-4">You&apos;ve earned a reward! Pick one:</p>
            {!claimView ? (
              <div className="space-y-2">
                {(biz.availableRewards ?? []).map((r) => (
                  <button
                    key={`${r.type}-${r.menuItemId ?? ''}`}
                    type="button"
                    onClick={() => { setSelectedReward(r); setClaimView(true); }}
                    className="block w-full py-3 rounded-xl border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/50 text-left px-4 font-medium"
                  >
                    {r.label}
                  </button>
                ))}
                {(!biz.availableRewards || biz.availableRewards.length === 0) && (
                  <p className="text-slate-500 dark:text-slate-400">No rewards configured.</p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600 dark:text-slate-400">Claim: {selectedReward?.label}</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setClaimView(false); setSelectedReward(null); }}
                    className="flex-1 py-2 rounded-lg border border-slate-300 dark:border-slate-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleClaim}
                    disabled={claiming}
                    className="flex-1 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium"
                  >
                    {claiming ? 'Claiming…' : 'Claim'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {!redeemed && biz.points > 0 && !hasUnredeemed && !canClaim && (
          <>
            <RewardsBar businessId={businessId} getToken={getToken} />
            <p className="text-slate-600 dark:text-slate-400 mt-3">Share menu or order food for more points.</p>
          </>
        )}

        {!redeemed && (!biz.points || biz.points === 0) && !hasUnredeemed && !canClaim && (
          <p className="text-slate-500 dark:text-slate-400">No rewards yet. Order from the menu to earn points.</p>
        )}
      </div>
    </div>
  );
}
